export function getFormFieldValue(formData, fieldName) {
    if (!Array.isArray(formData)) return null;
    const field = formData.find((item) => item.name === fieldName);
    return field ? field.value : null;
}

export function getCreatedAtFromFormData(formData) {
    if (!formData) {
        return "N/A";
    }

    if (Array.isArray(formData)) {
        const createdAtField = formData.find(
            (item) => item.name === "created_at",
        );
        return createdAtField ? createdAtField.value : "N/A";
    }

    return "N/A";
}

export function normalizeFileObject(fileData) {
    if (!fileData) return null;

    if (fileData instanceof File) return fileData;

    if (typeof fileData === "string") {
        return { id: fileData, isExisting: true };
    }

    return {
        id: fileData.id,
        url: fileData.file || fileData.url || fileData.download_url,
        name:
            fileData.file_name ||
            fileData.name ||
            fileData.original_name ||
            "uploaded_file",
        file_name:
            fileData.file_name || fileData.name || fileData.original_name,
        isExisting: true,
        ...fileData,
    };
}

function processFileValueForEdit(value) {
    return normalizeFileObject(value);
}

function processFieldValue(value, matchingField) {
    if (matchingField?.type === "date") {
        return formatDateForInput(value);
    }

    if (matchingField?.type === "file" || matchingField?.type === "image") {
        return processFileValueForEdit(value);
    }

    return value;
}

function processGroupFields(
    fields,
    groupId,
    selectedAssetType,
    formDataValues,
) {
    const groupFieldNames =
        selectedAssetType?.formConfig?.fields
            ?.filter((f) => f.groupId === groupId && f.type !== "group")
            ?.map((f) => f.name) || [];

    if (groupFieldNames.length > 0) {
        const fieldsByName = {};
        fields.forEach((field) => {
            if (!fieldsByName[field.name]) {
                fieldsByName[field.name] = [];
            }
            fieldsByName[field.name].push(field);
        });

        const maxSetCount = Math.max(
            ...Object.values(fieldsByName).map((arr) => arr.length),
            1,
        );

        for (let setIndex = 0; setIndex < maxSetCount; setIndex++) {
            groupFieldNames.forEach((fieldName) => {
                const fieldsForName = fieldsByName[fieldName];
                if (fieldsForName?.[setIndex]) {
                    const field = fieldsForName[setIndex];
                    const fieldKey = `field.group${groupId}.${setIndex}.${fieldName}`;
                    const matchingField =
                        selectedAssetType?.formConfig?.fields?.find(
                            (configField) => configField.name === fieldName,
                        );
                    formDataValues[fieldKey] = processFieldValue(
                        field.value,
                        matchingField,
                    );
                }
            });
        }
    } else {
        fields.forEach((field, index) => {
            const fieldKey = `field.group${groupId}.${index}.${field.name}`;
            const matchingField = selectedAssetType?.formConfig?.fields?.find(
                (configField) => configField.name === field.name,
            );
            formDataValues[fieldKey] = processFieldValue(
                field.value,
                matchingField,
            );
        });
    }
}

export function convertFormDataForEditing(formData, selectedAssetType) {
    const formDataValues = {};

    if (!formData || !Array.isArray(formData)) {
        return formDataValues;
    }

    const fieldsByGroup = {};
    const groupTypeFields = [];

    formData.forEach((field) => {
        const { groupId = "0", type } = field;

        if (type === "group" && Array.isArray(field.value)) {
            groupTypeFields.push(field);
        } else {
            if (!fieldsByGroup[groupId]) {
                fieldsByGroup[groupId] = [];
            }
            fieldsByGroup[groupId].push(field);
        }
    });

    Object.entries(fieldsByGroup).forEach(([groupId, fields]) => {
        if (groupId === "0") {
            fields.forEach((field) => {
                const fieldKey = `field.${field.name}`;
                const matchingField =
                    selectedAssetType?.formConfig?.fields?.find(
                        (configField) => configField.name === field.name,
                    );
                formDataValues[fieldKey] = processFieldValue(
                    field.value,
                    matchingField,
                );
            });
        } else {
            processGroupFields(
                fields,
                groupId,
                selectedAssetType,
                formDataValues,
            );
        }
    });

    groupTypeFields.forEach((field) => {
        const { groupId } = field;
        field.value.forEach((groupItems, groupIndex) => {
            if (Array.isArray(groupItems)) {
                groupItems.forEach((nestedField) => {
                    const fieldKey = `field.group${groupId}.${groupIndex}.${nestedField.name}`;
                    const matchingField =
                        selectedAssetType?.formConfig?.fields?.find(
                            (configField) =>
                                configField.name === nestedField.name,
                        );
                    formDataValues[fieldKey] = processFieldValue(
                        nestedField.value,
                        matchingField,
                    );
                });
            }
        });
    });

    return formDataValues;
}

export function formatDateForInput(dateString) {
    if (!dateString) return "";
    const dateMatch = dateString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (dateMatch) {
        const [, first, second, year] = dateMatch;
        if (parseInt(first) > 12) {
            const day = first;
            const month = second;
            return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
        } else if (parseInt(second) > 12) {
            const month = first;
            const day = second;
            return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
        } else {
            const day = first;
            const month = second;
            return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
        }
    }
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateString;
    }
    try {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
            return date.toISOString().split("T")[0];
        }
    } catch (e) {
        console.warn("Could not parse date:", dateString);
    }
    return dateString;
}
